<template>
  <IntegrationConfirmDialog
    ref="confirmDialogRef"
    @confirm="handleConfirmAction"
  />

  <IntegrationDetailsSidebar
    :visible="detailsSidebarVisible"
    :integration="detailsIntegration"
    :shop-integration-status="detailsIntegrationStatus"
    @update:visible="detailsSidebarVisible = $event"
    @install="handleInstall"
    @uninstall="handleUninstall"
    @toggle-status="handleToggleStatus"
    @configure="handleConfigureFromSidebar"
    @cancel-installation="handleCancelInstallation"
  />

  <IntegrationConfigModal
    ref="configModalRef"
    @done="reloadBothLists"
  />

  <div class="shop-integrations-page">
    <PageHeader
      :title="t('integrations.title')"
      :summary="t('integrations.summary')"
    />

    <div class="integrations-layout">
      <!-- ============================== -->
      <!-- SIDEBAR                        -->
      <!-- ============================== -->
      <aside class="sidebar">
        <nav class="sidebar-nav">
          <button
            class="nav-item"
            :class="{ active: activeView === 'installed' }"
            @click="activeView = 'installed'"
          >
            <div class="nav-icon-wrap">
              <i class="fas fa-plug" />
            </div>
            <div class="nav-text">
              <span class="nav-label">{{ t('integrations.sections.installed') }}</span>
              <span class="nav-subtitle">
                {{ activeTotalItems > 0
                  ? t('integrations.nav.installedCount', { count: activeTotalItems })
                  : t('integrations.nav.noneInstalled')
                }}
              </span>
            </div>
            <span
              v-if="activeTotalItems > 0"
              class="nav-badge"
            >{{ activeTotalItems }}</span>
          </button>
          <button
            class="nav-item"
            :class="{ active: activeView === 'marketplace' }"
            @click="activeView = 'marketplace'"
          >
            <div class="nav-icon-wrap">
              <i class="fas fa-store" />
            </div>
            <div class="nav-text">
              <span class="nav-label">{{ t('integrations.sections.marketplace') }}</span>
              <span class="nav-subtitle">{{ t('integrations.nav.discoverApps') }}</span>
            </div>
            <span
              v-if="availableTotalItems > 0"
              class="nav-badge"
            >{{ availableTotalItems }}</span>
          </button>
        </nav>

        <div class="sidebar-divider" />

        <!-- Categories -->
        <div class="sidebar-section">
          <h3 class="sidebar-section-title">
            {{ t('integrations.filters.categories') }}
          </h3>
          <div class="category-list">
            <label
              v-for="cat in categories"
              :key="cat.id"
              class="category-item"
              :class="{ selected: isCategorySelected(cat.id) }"
            >
              <input
                type="checkbox"
                :checked="isCategorySelected(cat.id)"
                @change="toggleCategory(cat.id)"
              >
              <span class="category-name">{{ getCategoryDisplayName(cat) }}</span>
            </label>
          </div>
          <button
            v-if="categoryFilter.length > 0"
            class="clear-filters-btn"
            @click="clearCategories"
          >
            <i class="fas fa-times" />
            {{ t('integrations.filters.clearAll') }}
          </button>
        </div>
      </aside>

      <!-- ============================== -->
      <!-- CONTENT                        -->
      <!-- ============================== -->
      <main class="content">
        <!-- ===== INSTALLED VIEW ===== -->
        <template v-if="activeView === 'installed'">
          <div class="content-header">
            <div class="content-header-row">
              <div class="search-wrapper">
                <i class="fas fa-search search-icon" />
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="t('integrations.search.placeholder')"
                  class="search-input"
                  @input="onSearchInput"
                >
                <button
                  v-if="searchQuery"
                  class="search-clear"
                  @click="clearSearch"
                >
                  <i class="fas fa-times" />
                </button>
              </div>
              <div class="status-filter">
                <button
                  class="status-filter-btn"
                  :class="{ active: statusFilter === 'all' }"
                  @click="statusFilter = 'all'"
                >
                  {{ t('integrations.filters.allStatuses') }}
                  <span
                    v-if="activeTotalItems > 0"
                    class="status-filter-count"
                  >{{ activeTotalItems }}</span>
                </button>
                <button
                  class="status-filter-btn"
                  :class="{ active: statusFilter === 'active' }"
                  @click="statusFilter = 'active'"
                >
                  <span class="status-dot active" />
                  {{ t('integrations.status.active') }}
                  <span
                    v-if="activeCount > 0"
                    class="status-filter-count"
                  >{{ activeCount }}</span>
                </button>
                <button
                  class="status-filter-btn"
                  :class="{ active: statusFilter === 'inactive' }"
                  @click="statusFilter = 'inactive'"
                >
                  <span class="status-dot" />
                  {{ t('integrations.status.inactive') }}
                  <span
                    v-if="inactiveCount > 0"
                    class="status-filter-count"
                  >{{ inactiveCount }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- G8 — état d'erreur inline + réessayer (au lieu du seul toast). -->
          <div
            v-if="errorActive"
            class="integrations-error"
          >
            <i class="fas fa-triangle-exclamation integrations-error__icon" />
            <p class="integrations-error__text">
              {{ t('integrations.errors.loadActive') }}
            </p>
            <button
              class="integrations-error__retry"
              @click="loadInstalledIntegrations"
            >
              <i class="fas fa-rotate-right" />
              {{ t('integrations.details.retry') }}
            </button>
          </div>

          <Loader v-else-if="loadingActive" />

          <div
            v-else-if="filteredActiveIntegrations.length === 0"
            class="empty-state"
          >
            <div class="empty-state-icon">
              <i class="fas fa-plug" />
            </div>
            <p class="empty-state-text">
              {{ statusFilter !== 'all' ? t('integrations.noFilteredIntegrations') : t('integrations.noActiveIntegrations') }}
            </p>
            <button
              v-if="statusFilter !== 'all'"
              class="empty-state-action"
              @click="statusFilter = 'all'"
            >
              <i class="fas fa-times" />
              {{ t('integrations.filters.clearAll') }}
            </button>
            <button
              v-else
              class="empty-state-action"
              @click="activeView = 'marketplace'"
            >
              <i class="fas fa-store" />
              {{ t('integrations.sections.browseMarketplace') }}
            </button>
          </div>

          <div
            v-else
            class="installed-list"
          >
            <div
              v-for="integration in filteredActiveIntegrations"
              :key="integration.id_integration"
              class="installed-row"
              :class="{
                'installed-row--active': getShopIntegrationStatus(integration) === 'active',
                'installed-row--inactive': getShopIntegrationStatus(integration) === 'inactive',
                'installed-row--pending': getShopIntegrationStatus(integration) === 'pending_auth',
              }"
            >
              <!-- Left: icon + info -->
              <div
                class="installed-row-left"
                @click="openDetails(integration)"
              >
                <div
                  class="installed-icon"
                  :class="{ 'has-image': integration.icon_url }"
                >
                  <img
                    v-if="integration.icon_url"
                    :src="integration.icon_url"
                    :alt="integration.name"
                  >
                  <i
                    v-else
                    class="fas fa-puzzle-piece"
                  />
                </div>
                <div class="installed-info">
                  <div class="installed-info-top">
                    <span class="installed-name">{{ integration.name }}</span>
                    <span
                      v-if="getPartnerName(integration)"
                      class="installed-partner"
                    >
                      {{ getPartnerName(integration) }}
                    </span>
                    <span
                      v-if="integration.visibility === 'restricted'"
                      class="installed-restricted"
                      :title="t('integrations.visibility.restrictedHint')"
                    >
                      <i class="fas fa-lock" />
                      {{ t('integrations.visibility.restricted') }}
                    </span>
                  </div>
                  <p
                    v-if="integration.short_description"
                    class="installed-description"
                  >
                    {{ integration.short_description }}
                  </p>
                </div>
              </div>

              <!-- Right: status + actions -->
              <div class="installed-row-right">
                <!-- Pending auth -->
                <template v-if="getShopIntegrationStatus(integration) === 'pending_auth'">
                  <span class="status-badge status-badge--pending">
                    <i class="fas fa-clock" />
                    {{ t('integrations.status.pendingAuth') }}
                  </span>
                  <button
                    class="btn-icon btn-icon--danger"
                    :title="t('integrations.actions.cancelInstallation')"
                    @click="handleCancelInstallation(integration)"
                  >
                    <i class="fas fa-times" />
                  </button>
                </template>

                <!-- Active / Inactive -->
                <template v-else>
                  <div class="status-toggle">
                    <span
                      class="status-dot"
                      :class="{ active: getShopIntegrationStatus(integration) === 'active' }"
                    />
                    <span class="status-label">
                      {{ getShopIntegrationStatus(integration) === 'active' ? t('integrations.status.active') : t('integrations.status.inactive') }}
                    </span>
                    <span :title="isActivationLocked(integration) ? t('integrations.activation.configureFirst') : ''">
                      <InputSwitch
                        :model-value="getShopIntegrationStatus(integration) === 'active'"
                        :disabled="isActivationLocked(integration)"
                        @update:model-value="handleToggleStatus(integration)"
                      />
                    </span>
                  </div>
                  <div class="installed-actions">
                    <button
                      v-if="hasConfig(integration)"
                      class="btn-icon"
                      :title="t('integrations.actions.configure')"
                      @click="navigateToConfigure(integration)"
                    >
                      <i class="fas fa-cog" />
                    </button>
                    <button
                      class="btn-icon"
                      :title="t('integrations.actions.viewDetails')"
                      @click="openDetails(integration)"
                    >
                      <i class="fas fa-info-circle" />
                    </button>
                    <button
                      class="btn-icon btn-icon--danger"
                      :title="t('integrations.actions.uninstall')"
                      @click="handleUninstall(integration)"
                    >
                      <i class="fas fa-trash-alt" />
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>

        <!-- ===== MARKETPLACE VIEW ===== -->
        <template v-else>
          <div class="content-header">
            <div class="search-wrapper">
              <i class="fas fa-search search-icon" />
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('integrations.search.placeholder')"
                class="search-input"
                @input="onSearchInput"
              >
              <button
                v-if="searchQuery"
                class="search-clear"
                @click="clearSearch"
              >
                <i class="fas fa-times" />
              </button>
            </div>
          </div>

          <!-- G8 — état d'erreur inline + réessayer. -->
          <div
            v-if="errorAvailable"
            class="integrations-error"
          >
            <i class="fas fa-triangle-exclamation integrations-error__icon" />
            <p class="integrations-error__text">
              {{ t('integrations.errors.loadAvailable') }}
            </p>
            <button
              class="integrations-error__retry"
              @click="loadCatalogue"
            >
              <i class="fas fa-rotate-right" />
              {{ t('integrations.details.retry') }}
            </button>
          </div>

          <!-- G8 — skeleton cards pendant le chargement (au lieu d'un simple spinner). -->
          <div
            v-else-if="loadingAvailable"
            class="marketplace-grid"
          >
            <div
              v-for="n in 6"
              :key="`skeleton-${n}`"
              class="marketplace-skeleton"
            >
              <div class="marketplace-skeleton__thumb" />
              <div class="marketplace-skeleton__line marketplace-skeleton__line--title" />
              <div class="marketplace-skeleton__line" />
              <div class="marketplace-skeleton__line marketplace-skeleton__line--short" />
            </div>
          </div>

          <div
            v-else-if="availableIntegrations.length === 0"
            class="empty-state"
          >
            <div class="empty-state-icon">
              <i class="fas fa-puzzle-piece" />
            </div>
            <p class="empty-state-text">
              {{ t('integrations.noAvailableIntegrations') }}
            </p>
          </div>

          <template v-else>
            <div class="marketplace-grid">
              <IntegrationCard
                v-for="integration in availableIntegrations"
                :key="integration.id_integration"
                :integration="integration"
                :is-installed="isIntegrationInstalled(integration)"
                :shop-integration-status="getShopIntegrationStatus(integration)"
                @install="handleInstall"
                @cancel-installation="handleCancelInstallation"
                @view-details="openDetails"
              />
            </div>

            <IntegrationPagination
              v-model:current-page="currentPage"
              :total-items="availableTotalItems"
              :rows-per-page="rowsPerPage"
            />
          </template>
        </template>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, onUnmounted, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { showToastError, showToastSuccess } from '@/helpers';
import PageHeader from '@/components/layout/PageHeader.vue';
import InputSwitch from 'primevue/inputswitch';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { useIntegrations } from '@/composables/useIntegrations';
import type { Integration } from '@/composables/useIntegrations';
import { pickLocalizedText, parseCategoryName } from '@/utils/localized-text';
import type { LocalizedText } from '@/utils/localized-text';
import Loader from '@/components/layout/Loader.vue';
import IntegrationPagination from './components/IntegrationPagination.vue';
import IntegrationCard from './components/IntegrationCard.vue';
import IntegrationConfirmDialog from './components/IntegrationConfirmDialog.vue';
import IntegrationDetailsSidebar from './components/IntegrationDetailsSidebar.vue';
import IntegrationConfigModal from './components/IntegrationConfigModal.vue';

interface Category {
  id: string | number;
  name: LocalizedText;
}

export default defineComponent({
  name: 'ShopIntegrations',

  components: {
    PageHeader,
    InputSwitch,
    IntegrationPagination,
    IntegrationCard,
    IntegrationConfirmDialog,
    IntegrationDetailsSidebar,
    IntegrationConfigModal,
    Loader,
  },

  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const router = useRouter();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const confirmDialogRef = ref<any>(null);
    const configModalRef = ref<any>(null);

    const idShop = UserState.activeShop?.id ?? 0;

    const {
      activeIntegrations,
      availableIntegrations,
      loadingActive,
      loadingAvailable,
      activeTotalItems,
      availableTotalItems,
      errorActive,
      errorAvailable,
      loadIntegrations,
      installIntegration,
      uninstallIntegration,
      toggleIntegrationStatus,
      cancelInstallation,
      isIntegrationInstalled,
      getShopIntegrationStatus,
      getIntegrationDetails,
    } = useIntegrations(idShop);

    // --- View state ---
    const activeView = ref<'installed' | 'marketplace'>('marketplace');
    const statusFilter = ref<'all' | 'active' | 'inactive'>('all');

    // --- Filtered integrations ---
    const filteredActiveIntegrations = computed(() => {
      if (statusFilter.value === 'all') return activeIntegrations.value;
      return activeIntegrations.value.filter(
        (i) => getShopIntegrationStatus(i) === statusFilter.value,
      );
    });

    const activeCount = computed(() => activeIntegrations.value.filter(
      (i) => getShopIntegrationStatus(i) === 'active',
    ).length);

    const inactiveCount = computed(() => activeIntegrations.value.filter(
      (i) => getShopIntegrationStatus(i) === 'inactive',
    ).length);

    // --- Details sidebar ---
    const detailsSidebarVisible = ref(false);
    const detailsIntegration = ref<Integration | null>(null);

    const detailsIntegrationStatus = computed(() => {
      if (!detailsIntegration.value) return '';
      return getShopIntegrationStatus(detailsIntegration.value);
    });

    const openDetails = (integration: Integration) => {
      detailsIntegration.value = integration;
      detailsSidebarVisible.value = true;
    };

    const handleConfigureFromSidebar = (integration: Integration) => {
      detailsSidebarVisible.value = false;
      navigateToConfigure(integration);
    };

    // --- Categories ---
    const categories = ref<Category[]>([]);
    const categoryFilter = ref<(string | number)[]>([]);

    const loadCategories = async () => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/integrations/categories',
          {},
          '',
          undefined,
          true,
          { fields: 'id_category,category_name' },
        );

        if (response?.data) {
          categories.value = response.data
            .map((cat: { id_category?: string | number; category_name: string }) => {
              // Parseur `category_name` centralisé (G12).
              const parsedName = parseCategoryName(cat.category_name);
              if (!parsedName) return null;
              return { id: cat.id_category ?? parsedName.fr ?? '', name: parsedName };
            })
            .filter((cat: Category | null): cat is Category => cat !== null);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    const getCategoryDisplayName = (cat: Category): string => pickLocalizedText(cat.name, locale.value as string);

    const isCategorySelected = (catId: string | number): boolean => categoryFilter.value.includes(catId);

    const toggleCategory = (catId: string | number) => {
      const index = categoryFilter.value.indexOf(catId);
      if (index === -1) {
        categoryFilter.value = [...categoryFilter.value, catId];
      } else {
        categoryFilter.value = categoryFilter.value.filter((id) => id !== catId);
      }
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    const clearCategories = () => {
      categoryFilter.value = [];
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    // --- Search ---
    const searchQuery = ref('');
    let searchTimeout: NodeJS.Timeout | null = null;

    const onSearchInput = () => {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        reloadActiveView(); // eslint-disable-line no-use-before-define
      }, 500);
    };

    const clearSearch = () => {
      searchQuery.value = '';
      currentPage.value = 1;
      reloadActiveView(); // eslint-disable-line no-use-before-define
    };

    // --- Pagination ---
    const rowsPerPage = ref(12);
    const currentPage = ref(1);

    watch(currentPage, () => {
      loadCatalogue(); // eslint-disable-line no-use-before-define
    });

    // --- Data loading ---

    const loadInstalledIntegrations = () => loadIntegrations(
      'active',
      searchQuery.value,
      categoryFilter.value,
      1,
      100,
    );

    const loadCatalogue = () => loadIntegrations(
      'available',
      searchQuery.value,
      categoryFilter.value,
      currentPage.value,
      rowsPerPage.value,
    );

    const reloadActiveView = () => {
      if (activeView.value === 'installed') {
        loadInstalledIntegrations();
      } else {
        loadCatalogue();
      }
    };

    // --- Helpers ---

    const getPartnerName = (integration: Integration): string => {
      if (!integration.partners_data || integration.partners_data.length === 0) return '';
      return integration.partners_data[0].partner_name || '';
    };

    const hasConfig = (integration: Integration): boolean => !!(
      (integration.config?.fields && integration.config.fields.length > 0)
      || (integration.config?.steps && integration.config.steps.length > 0)
    );

    // T1 — lock the activate toggle for an installed-but-unconfigured integration
    // (config schema present, not completed / connection not verified).
    const isActivationLocked = (integration: Integration): boolean => getShopIntegrationStatus(integration) === 'inactive'
      && hasConfig(integration) && !integration.configured;

    const navigateToConfigure = (integration: Integration) => {
      // T3 — open the configuration as a modal instead of navigating to a page.
      configModalRef.value?.open(integration);
    };

    // --- Action handlers ---

    const handleInstall = (integration: Integration): void => {
      // Defensive guard: `soon` integrations must never be installed even if
      // the call slipped through (e.g. legacy emit or external trigger).
      if (integration.status === 'soon') {
        return;
      }
      const action = integration.external_auth === '1' ? 'installOAuth' : 'install';
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, action);
      }
    };

    const handleUninstall = (integration: Integration): void => {
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, 'uninstall');
      }
    };

    const handleToggleStatus = (integration: Integration): void => {
      const currentStatus = getShopIntegrationStatus(integration);
      const action = currentStatus === 'active' ? 'deactivate' : 'activate';
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, action);
      }
    };

    const handleCancelInstallation = (integration: Integration): void => {
      if (confirmDialogRef.value) {
        confirmDialogRef.value.open(integration, 'cancel');
      }
    };

    const reloadBothLists = async () => {
      await loadInstalledIntegrations();
      await loadCatalogue();
    };

    const handleConfirmAction = async ({ integration, action }: { integration: Integration; action: string }) => {
      try {
        switch (action) {
          case 'install':
          case 'installOAuth':
            {
              const result = await installIntegration(integration);
              if (result.notFound) {
                // L'intégration n'est plus proposée à cette boutique (retirée du
                // catalogue ou portée restreinte) : on ferme la fiche et on
                // rafraîchit les listes pour retirer la carte périmée.
                detailsSidebarVisible.value = false;
                await reloadBothLists();
                return;
              }
              if (action === 'installOAuth' && result.redirectUrl) {
                window.open(result.redirectUrl, '_blank');
              }
              if (result.requiresOAuth) {
                // OAuth flow: the integration stays in pending_auth until the
                // provider redirects back. Refresh the lists and stop here —
                // do NOT open the config modal prematurely.
                await reloadBothLists();
                return;
              }
              if (result.success) {
                // T3 — refresh the lists then open the config modal (no full
                // page reload / navigation to a separate config page).
                await reloadBothLists();
                configModalRef.value?.open(integration);
                return;
              }
            }
            break;
          case 'uninstall':
            {
              const success = await uninstallIntegration(integration);
              if (success) {
                // Full reload to marketplace (sources may have been removed)
                window.location.href = router.resolve({
                  name: 'shop.Integrations',
                }).href;
                return;
              }
            }
            break;
          case 'activate':
          {
            await toggleIntegrationStatus(integration);
            // T5 — activation is only reachable once the integration is fully
            // configured (see T1 gating), so there is no reason to re-open the
            // config form afterwards: return to the marketplace list (full
            // reload — sources may have changed).
            window.location.href = router.resolve({
              name: 'shop.Integrations',
            }).href;
            return;
          }
          case 'deactivate':
          {
            await toggleIntegrationStatus(integration);
            // Full reload to marketplace (sources may have changed)
            window.location.href = router.resolve({
              name: 'shop.Integrations',
            }).href;
            return;
          }
          case 'cancel':
            {
              const success = await cancelInstallation(integration);
              if (success) {
                await reloadBothLists();
              }
            }
            break;
          default:
            break;
        }
      } finally {
        if (confirmDialogRef.value) {
          confirmDialogRef.value.finishLoading();
        }
      }
    };

    // --- OAuth callback ---

    const checkOAuthCallback = () => {
      const installStatus = route.query.install_status as string;
      const error = route.query.error as string;

      if (installStatus === 'error' && error) {
        let errorMessage = t('integrations.errors.installationFailed');
        switch (error) {
          case 'internal_error':
            errorMessage = t('integrations.errors.internalError');
            break;
          case 'auth_failed':
            errorMessage = t('integrations.errors.authFailed');
            break;
          case 'invalid_credentials':
            errorMessage = t('integrations.errors.invalidCredentials');
            break;
          case 'access_denied':
            errorMessage = t('integrations.errors.accessDenied');
            break;
          default:
            errorMessage = t('integrations.errors.genericError');
        }
        showToastError(errorMessage, 5000);
        router.replace({ query: {} });
      } else if (installStatus === 'success') {
        showToastSuccess(t('integrations.success.installed'), 5000);
        router.replace({ query: {} });
      }
    };

    // --- Mount ---

    /**
     * `?focus=slug` (G6) : point d'entrée profond depuis un CTA marketplace.
     * On bascule sur la vue Marketplace, on pré-remplit la recherche avec le
     * slug (pour surfacer la carte) et on ouvre la fiche détaillée si le slug
     * résout une intégration. Best-effort : un slug inconnu ne fait qu'amorcer
     * la recherche, sans erreur bloquante.
     */
    const handleFocusQuery = async () => {
      const rawFocus = route.query.focus;
      const slug = Array.isArray(rawFocus) ? rawFocus[0] : rawFocus;
      if (!slug) return;
      activeView.value = 'marketplace';
      searchQuery.value = String(slug);
      loadCatalogue();
      const details = await getIntegrationDetails(String(slug));
      if (details) {
        openDetails(details);
      }
    };

    // G8 — au switch de vue (installées ⇄ marketplace), on réinitialise la
    // recherche pour ne pas transporter un filtre d'une vue à l'autre (contexte
    // et jeux de données différents), puis on (re)charge la vue cible.
    watch(activeView, () => {
      if (searchQuery.value) searchQuery.value = '';
      reloadActiveView();
    });

    onMounted(() => {
      checkOAuthCallback();
      loadInstalledIntegrations();
      loadCatalogue();
      loadCategories();
      handleFocusQuery();
    });

    onUnmounted(() => {
      if (searchTimeout) clearTimeout(searchTimeout);
    });

    return {
      t,
      confirmDialogRef,
      configModalRef,
      reloadBothLists,
      activeView,
      statusFilter,
      filteredActiveIntegrations,
      activeCount,
      inactiveCount,
      // Installed
      activeIntegrations,
      loadingActive,
      activeTotalItems,
      errorActive,
      errorAvailable,
      loadInstalledIntegrations,
      loadCatalogue,
      getShopIntegrationStatus,
      getPartnerName,
      hasConfig,
      isActivationLocked,
      navigateToConfigure,
      // Marketplace
      availableIntegrations,
      loadingAvailable,
      availableTotalItems,
      searchQuery,
      onSearchInput,
      clearSearch,
      currentPage,
      rowsPerPage,
      isIntegrationInstalled,
      // Categories
      categories,
      categoryFilter,
      getCategoryDisplayName,
      isCategorySelected,
      toggleCategory,
      clearCategories,
      // Details sidebar
      detailsSidebarVisible,
      detailsIntegration,
      detailsIntegrationStatus,
      openDetails,
      handleConfigureFromSidebar,
      // Actions
      handleInstall,
      handleUninstall,
      handleToggleStatus,
      handleCancelInstallation,
      handleConfirmAction,
    };
  },
});
</script>

<style scoped lang="scss">
.shop-integrations-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* ============================== */
/* Two-column layout              */
/* ============================== */
.integrations-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* ============================== */
/* SIDEBAR                        */
/* ============================== */
.sidebar {
  border-top: solid 2px #94c840;
  width: 240px;
  flex-shrink: 0;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  padding: 1.25rem 0;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: calc(100vh - 80px);
  overflow-y: auto;

  @media (max-width: 900px) {
    width: 56px;
    padding: 1rem 0;

    .nav-label,
    .nav-badge,
    .sidebar-section,
    .sidebar-divider {
      display: none;
    }

    .nav-item {
      justify-content: center;
      padding: 0.625rem;
      margin: 0 0.5rem;
    }
  }
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.625rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  color: #6b7280;
  width: 100%;
  text-align: left;
  position: relative;

  &:hover {
    background: #f9fafb;
    color: #374151;
  }

  &.active {
    background: rgba(148, 200, 64, 0.1);
    color: #1f2937;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.5rem;
      bottom: 0.5rem;
      width: 3px;
      background: #94c840;
      border-radius: 0 3px 3px 0;
    }

    .nav-icon-wrap {
      color: #94c840;
    }

    .nav-label {
      font-weight: 600;
    }

    .nav-subtitle {
      color: #6b7280;
    }
  }
}

.nav-icon-wrap {
  flex-shrink: 0;
  width: 1.25rem;
  text-align: center;
  font-size: 0.9375rem;
  color: #9ca3af;
}

.nav-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.nav-label {
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-subtitle {
  font-size: 0.6875rem;
  color: #9ca3af;
  white-space: nowrap;
  margin-top: 0.0625rem;
}

.nav-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  padding: 0 0.375rem;
  background: #f3f4f6;
  color: #9ca3af;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  flex-shrink: 0;

  .active & {
    background: #94c840;
    color: #ffffff;
  }
}

.sidebar-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 1rem 1rem;
}

/* Categories */
.sidebar-section {
  padding: 0 0.625rem;
}

.sidebar-section-title {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin: 0 0 0.5rem 1rem;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8125rem;
  color: #6b7280;

  input[type="checkbox"] {
    width: 0.9375rem;
    height: 0.9375rem;
    border-radius: 3px;
    border: 1.5px solid #d1d5db;
    cursor: pointer;
    accent-color: #94c840;
    flex-shrink: 0;
  }

  &:hover {
    background: #f9fafb;
    color: #374151;
  }

  &.selected {
    color: #1f2937;
    font-weight: 500;
    background: rgba(148, 200, 64, 0.1);
  }
}

.category-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  margin-top: 0.5rem;
  background: transparent;
  border: none;
  color: #94c840;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 4px;

  i {
    font-size: 0.625rem;
  }

  &:hover {
    background: rgba(148, 200, 64, 0.1);
    color: #7fb32e;
  }
}

/* ============================== */
/* CONTENT                        */
/* ============================== */
.content {
  flex: 1;
  min-width: 0;
  padding: 1.5rem;
  padding-top: 0;
  overflow-y: auto;
}

.content-header {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  margin-bottom: 1.5rem;
}

.content-header-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Status filter */
.status-filter {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 0.1875rem;
}

.status-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    color: #374151;
  }

  &.active {
    background: #ffffff;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .status-dot {
    width: 0.4375rem;
    height: 0.4375rem;
  }
}

.status-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 0.25rem;
  background: #e5e7eb;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #6b7280;

  .status-filter-btn.active & {
    background: #94c840;
    color: #ffffff;
  }
}

.content-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

/* Search */
.search-wrapper {
  position: relative;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 0.8125rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2.25rem 0.5rem 2.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #1f2937;
  background: #ffffff;
  transition: all 0.15s ease;
  height: 2.375rem;

  &:focus {
    outline: none;
    border-color: #94c840;
    box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.search-clear {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  transition: all 0.15s ease;

  &:hover {
    color: #6b7280;
    background: #f3f4f6;
  }

  i {
    font-size: 0.6875rem;
  }
}

/* ============================== */
/* INSTALLED VIEW                 */
/* ============================== */
.installed-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.installed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 0.875rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &--active {
    border-left: 3px solid #94c840;
  }

  &--inactive {
    border-left: 3px solid #d1d5db;
  }

  &--pending {
    border-left: 3px solid #f59e0b;
    background: #fffbeb;
  }
}

.installed-row-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  border-radius: 8px;
  padding: 0.25rem;
  margin: -0.25rem;
  transition: background 0.15s ease;

  &:hover {
    background: #f9fafb;
  }
}

.installed-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  border-radius: 0.5rem;
  color: #ffffff;
  font-size: 1.125rem;
  overflow: hidden;

  &.has-image {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    padding: 0.125rem;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 0.375rem;
  }
}

.installed-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.installed-info-top {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.installed-name {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
}

.installed-partner {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
}

.installed-restricted {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.0625rem 0.4375rem;
  background: #f3e8ff;
  color: #6b21a8;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.5625rem;
  }
}

.installed-description {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.installed-row-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;
  min-width: 220px;
}

.status-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #d1d5db;
  transition: background 0.2s ease;

  &.active {
    background: #22c55e;
    box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
  }
}

.status-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;

  i {
    font-size: 0.625rem;
  }

  &--pending {
    background: #fef3c7;
    color: #b45309;
  }
}

.installed-actions {
  display: flex;
  align-items: center;
  gap: 0.125rem;
}

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8125rem;
  text-decoration: none;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  &--danger:hover {
    background: #fef2f2;
    color: #dc2626;
  }
}

/* ============================== */
/* MARKETPLACE VIEW               */
/* ============================== */
.marketplace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

/* ============================== */
/* Skeleton cards (G8)            */
/* ============================== */
.marketplace-skeleton {
  border: 1px solid #eef0f5;
  border-radius: 14px;
  padding: 1.25rem;
  background: #fff;

  &__thumb {
    height: 120px;
    border-radius: 10px;
    margin-bottom: 1rem;
    background: #eef0f5;
  }

  &__line {
    height: 12px;
    border-radius: 6px;
    margin-bottom: 0.625rem;
    background: #eef0f5;

    &--title { width: 60%; height: 16px; }
    &--short { width: 40%; }
  }

  &__thumb,
  &__line {
    position: relative;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
      animation: marketplace-skeleton-shimmer 1.3s infinite;
    }
  }
}

@keyframes marketplace-skeleton-shimmer {
  100% { transform: translateX(100%); }
}

/* ============================== */
/* Inline error state (G8)        */
/* ============================== */
.integrations-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2.5rem 1.5rem;
  text-align: center;

  &__icon {
    font-size: 2rem;
    color: #ef4444;
  }

  &__text {
    margin: 0;
    font-size: 0.9375rem;
    color: #6b7280;
  }

  &__retry {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1.125rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    color: #374151;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #94c840;
      color: #5d8a1e;
    }
  }
}

/* ============================== */
/* Empty state                    */
/* ============================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1rem;
}

.empty-state-icon {
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 50%;
  color: #9ca3af;

  i {
    font-size: 1.5rem;
  }
}

.empty-state-text {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0;
}

.empty-state-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #94c840;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #7fb32e;
  }

  i {
    font-size: 0.75rem;
  }
}
</style>
